import React from 'react';
import { useFilePreview } from '../hooks/useFilePreview';
import { FilePreviewProps } from '../types';
import { viewerRegistry } from '../registry/ViewerRegistry';
import { cn } from '@/common/utils';
import { Loader2 } from 'lucide-react';

/**
 * FilePreview Component
 *
 * Renders the appropriate viewer for the current file.
 * Must be used within a FilePreviewProvider.
 *
 * Takes up 100% of container width and height by default.
 *
 * @example
 * ```tsx
 * <FilePreviewProvider>
 *   <div className="h-screen">
 *     <FilePreview />
 *   </div>
 * </FilePreviewProvider>
 * ```
 */
export const FilePreview: React.FC<FilePreviewProps> = ({
  className,
  loading = false,
  loadingComponent,
  unsupportedComponent,
}) => {
  const { isOpen, fileUrl, fileType, fileName, close } = useFilePreview();

  // Don't render anything if not open
  if (!isOpen || !fileUrl) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className={cn('w-full h-full flex items-center justify-center bg-gray-50', className)}>
        {loadingComponent || (
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        )}
      </div>
    );
  }

  // Get the appropriate viewer component
  const ViewerComponent = viewerRegistry.getViewer(fileUrl, fileType);

  if (!ViewerComponent) {
    // No viewer found - show unsupported message
    return (
      <div className={cn('w-full h-full flex items-center justify-center bg-gray-50', className)}>
        {unsupportedComponent || (
          <div className="text-center text-gray-500">
            <p className="text-sm">No viewer available for this file type</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full h-full', className)}>
      <ViewerComponent
        fileUrl={fileUrl}
        fileName={fileName || undefined}
        onClose={close}
      />
    </div>
  );
};

export default FilePreview;
